import { useAtomSet, useAtomValue } from '@effect/atom-react';
import { Schema } from 'effect';
import { atomKvsSync, kvsSyncReader } from '../atoms/kvs';

/**
 * How the user wants their audio library ordered. Device-local (web
 * localStorage / mobile MMKV) rather than a `user_preferences` column: nothing
 * on the backend acts on it, and the library request has to carry the right
 * sort parameters from its very first fetch — a server-stored value would have
 * to be resolved before the home route loader could even start the list
 * prefetch.
 *
 * Every mode maps to an ordering the server produces (see
 * {@link librarySortParams}), so no mode needs the whole library fetched before
 * it is correct. `continue-first` rides the newest ordering; web pins the
 * continue-listening rows on top of it client-side.
 */
export const librarySortModes = [
  'newest',
  'oldest',
  'a-z',
  'z-a',
  'continue-first',
] as const;

export type LibrarySortMode = (typeof librarySortModes)[number];

/** Where the sort is stored. */
export const librarySortModeKey = 'library-sort-mode';

const librarySortModeKvs = {
  key: librarySortModeKey,
  schema: Schema.Literals(librarySortModes),
  defaultValue: (): LibrarySortMode => 'newest',
};

export const librarySortModeAtom = atomKvsSync(librarySortModeKvs);

/**
 * Non-React read of the same stored value, for the query keys and cache
 * lookups built outside a component (route loaders, the play bar's neighbor
 * walk, mobile's offline-library hydration). Reads the storage the atom
 * persists to, so the two can never disagree.
 */
export const getLibrarySortMode = kvsSyncReader(librarySortModeKvs);

export const useLibrarySortMode = () => useAtomValue(librarySortModeAtom);

export const useSetLibrarySortMode = () => useAtomSet(librarySortModeAtom);

/** The `sort_by` / `sort_dir` the audio-list request carries for a mode. */
export const librarySortParams = (mode: LibrarySortMode) => {
  switch (mode) {
    case 'oldest': {
      return { sort_by: 'created_at', sort_dir: 'asc' } as const;
    }
    case 'a-z': {
      return { sort_by: 'title', sort_dir: 'asc' } as const;
    }
    case 'z-a': {
      return { sort_by: 'title', sort_dir: 'desc' } as const;
    }
    case 'newest':
    case 'continue-first': {
      return { sort_by: 'created_at', sort_dir: 'desc' } as const;
    }
    default: {
      return mode satisfies never;
    }
  }
};
